import React, { ReactNode, useCallback, useContext, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

// Context
import { AppContext } from "@vahak/core/dist/app-context";

// Types
import { MarketType } from "@vahak/core/dist/custom-types/market-type";
import { LorryListTypes } from "@vahak/core/dist/custom-types/lorry";
import { IEventTrackerProp } from "../../../../types/events";

// Components
import AppBtn from "@vahak/core/dist/components/app-btn/AppBtn";
import { toast } from "@vahak/core/dist/components/toast/toast";
import LorryReactivateModal from "./lorry/LorryReactivateModal";
import {
    getLorryVehicleRestriction,
    VehicleRestrictionKind,
    VehicleRestrictionPopup
} from "../../../posting/vehicle-restriction-popup";

// Style
import Styles from "./ReactivateLoadLorry.module.scss";

// Icons
import ReactiveIcon from "@vahak/core/dist/icons/reactivate.svg";

// Constant
import { lorryFormValidations } from "../../../posting/helpers/validations/lorryFormValidations";
import { LORRY_CREATION_FORM_IDS, lorryCreationFormFieldNames } from "../../../posting/helpers/constants";
import { LorryScreenEvents } from "@vahak/core/dist/constants/event-names";
import { MARKET_TYPE } from "@vahak/core/dist/constants/market";
// Services
import { useReactivateLorry } from "@vahak/core/dist/_services/hooks/usePostingService";

//Hooks
import useValidateUserStatus from "../../../../hooks/useValidateUserStatus";
import { useToggleModalWithLocalState } from "@vahak/core-ui/dist/hooks";
import { PlaceSearchResult } from "@vahak/core/dist/custom-types/location-search";
import { QueryNames } from "@vahak/core/dist/_services/hooks/queryNames";
import { useQueryClient } from "react-query";
import { LORRY_POSTING_FORM_LOCATION_STATE } from "../../../posting/lorry-posting/helpers/constants";
import { useRouter } from "next/router";
import { generataMaskUrlWithParams } from "../../../../methods/generataMaskUrlWithParams";
import {
    ILoadLPostingContextData,
    ILorryPostingContextData,
    POSTING_FORM_MODAL_ORIGINATION_PAGE,
    useLoadLorryPosting
} from "../../../../context/load-lorry-posting";

// Interface
interface ReactivateLoadLorryProps extends IEventTrackerProp {
    type: MarketType;
    data: any;
    reactivateButton?: ReactNode;
    onSuccess?: (val?: any) => void;
    handleOpenEditModal?: (
        category: ILorryPostingContextData["category"] | ILoadLPostingContextData["category"]
    ) => void;
}

const ReactivateLoadLorry = ({
    type,
    data,
    reactivateButton,
    onSuccess,
    trackEvent,
    handleOpenEditModal
}: ReactivateLoadLorryProps) => {
    const { companyId } = useContext(AppContext);
    const { t } = useTranslation(["userProfile", "common"]);
    const [activeCard, setActiveCard] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [restrictionKind, setRestrictionKind] = useState<VehicleRestrictionKind | null>(null);
    const { updateLoadLorryPostingData } = useLoadLorryPosting();

    /**
     * Lorry Activation
     */
    const { isModalOpen, toggleModal } = useToggleModalWithLocalState();

    const handleReactivateLorry = () => {
        setActiveCard(data.lorry_detail.id);
        toggleModal();
    };

    const lorryReactivationSuccess = () => {
        /**
         * Events
         */

        toggleModal();
        onSuccess?.(data.lorry_detail.id);
    };

    /**
     * Load posting verification check
     */
    const { ValidateLoadPosting, ValidateLorryPosting } = useValidateUserStatus();

    const { mutateAsync: reactivateLorry } = useReactivateLorry();
    const queryClient = useQueryClient();
    const router = useRouter();

    const handleActivation = async (place: PlaceSearchResult) => {
        /**
         * Events
         */

        const currentPlaceID = place?.place_id ?? "";
        reactivateLorry(
            { lorryId: data.lorry_id || data.lorry_detail.id, currentPlaceID },
            {
                onSuccess: (res) => {
                    toast.success("Lorry reactivated successfully.");
                    queryClient.invalidateQueries([QueryNames.userLorries.getLorriesByCategory, companyId]);
                    lorryReactivationSuccess?.();
                },
                onError: (res) => {
                    toast.error(res.message);
                }
            }
        );
    };

    const handleRestrictionCancel = useCallback(() => {
        setRestrictionKind(null);
    }, []);

    const handleRestrictionAddNewVehicle = useCallback(
        (e?: React.MouseEvent) => {
            e?.stopPropagation();
            e?.preventDefault();

            // Drop details-page id so dashboard does not open Details on this navigation.
            delete router.query.id;
            router.query[LORRY_POSTING_FORM_LOCATION_STATE.PARAM_NAME] = LORRY_CREATION_FORM_IDS.VEHICLE_NUMBER;

            ValidateLorryPosting?.(() => {
                updateLoadLorryPostingData({
                    category: "post",
                    isModalOpen: true,
                    type: "lorry",
                    data: undefined,
                    originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE.DASHBOARD
                });
                router.push({ query: router.query }, generataMaskUrlWithParams(router.query), {
                    shallow: true,
                    scroll: false
                });
                // Delay unmount so the click cannot fall through to the lorry card (opens details for CS).
                window.setTimeout(() => setRestrictionKind(null), 0);
            });
        },
        [ValidateLorryPosting, router, updateLoadLorryPostingData]
    );

    const handleReactivateClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        trackEvent?.(LorryScreenEvents["reactivate_lorry_clicked"]);
        const { lorry_detail } = (data ?? {}) as LorryListTypes;
        const lorryNumberSchema = lorryFormValidations[LORRY_CREATION_FORM_IDS.VEHICLE_NUMBER];
        if (!lorry_detail?.number) {
            router.query[LORRY_POSTING_FORM_LOCATION_STATE.PARAM_NAME] = LORRY_CREATION_FORM_IDS.VEHICLE_NUMBER;

            return ValidateLorryPosting?.(() => {
                router.push({ query: router.query }, generataMaskUrlWithParams(router.query), {
                    shallow: true
                });
            });
        }
        const lorryNumberData = {
            [lorryCreationFormFieldNames.lorryNum]: lorry_detail?.number
        };
        if (!(await lorryNumberSchema.isValid(lorryNumberData))) {
            return toast.error("Invalid lorry number");
        }

        const restriction = getLorryVehicleRestriction({
            capacity: lorry_detail?.capacity,
            totalTyres: lorry_detail?.total_tyres
        });

        if (restriction === "three_wheeler") {
            setRestrictionKind("three_wheeler_lorry");
            return;
        }

        if (restriction === "capacity_out_of_range") {
            setRestrictionKind("capacity_reactivate");
            return;
        }

        // validate and switch b/w edit modal or current location
        const dataToValidate = {
            [lorryCreationFormFieldNames.lorryType]: lorry_detail?.type_value,
            [lorryCreationFormFieldNames.lorryCapacity]: lorry_detail?.capacity,
            [lorryCreationFormFieldNames.lorryTyres]: lorry_detail?.total_tyres,
            [lorryCreationFormFieldNames.lorrySize]: lorry_detail?.size,
            [lorryCreationFormFieldNames.sizeText]: lorry_detail?.size_text,
            [lorryCreationFormFieldNames.lorryBodyType]: lorry_detail?.body_type
        };

        const schema = lorryFormValidations[LORRY_CREATION_FORM_IDS.VEHICLE_TYPE];
        schema
            .validate(dataToValidate)
            .then(() => {
                ValidateLorryPosting?.(() => handleOpenEditModal?.("re-post"));
            })
            .catch(() => {
                ValidateLorryPosting?.(() => handleOpenEditModal?.("edit"));
            });
    };

    return (
        <>
            {type === MARKET_TYPE.LOAD || type === MARKET_TYPE.LOADS ? (
                <div className={classnames(Styles.reactivateBtnWrapper)} onClick={(e) => e.stopPropagation()}>
                    {reactivateButton ? (
                        <>{reactivateButton}</>
                    ) : (
                        <AppBtn
                            className={Styles.reactivateBtn}
                            fillType="outline"
                            onClick={(e) => {
                                setIsSubmitting(true);
                                e.stopPropagation();
                                ValidateLoadPosting?.(async () => {
                                    handleOpenEditModal?.("re-post");
                                    setIsSubmitting(false);
                                });
                            }}
                            disabled={activeCard !== 0 || isSubmitting}
                        >
                            <ReactiveIcon /> &nbsp; Re-post
                        </AppBtn>
                    )}
                </div>
            ) : (
                <div className={classnames(Styles.reactivateBtnWrapper)} onClick={(e) => e.stopPropagation()}>
                    {reactivateButton ? (
                        <>{reactivateButton}</>
                    ) : (
                        <AppBtn
                            className={Styles.reactivateBtn}
                            fillType="outline"
                            onClick={(e) => {
                                void handleReactivateClick(e);
                            }}
                        >
                            <ReactiveIcon /> Re-activate
                        </AppBtn>
                    )}
                    {/* Lorry activation */}
                    {/* {activeCard === data.lorry_detail?.id && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <LorryReactivateModal
                                isOpen={isModalOpen}
                                onToggle={toggleModal}
                                titleText={`${t("userProfile:my_lorries.lorry_list.enter_curr_loc_to_activate_lorry")}`}
                                onAction={handleActivation}
                            />
                        </div>
                    )} */}
                </div>
            )}

            {restrictionKind ? (
                <VehicleRestrictionPopup
                    id="lorry-reactivate-vehicle-restriction-popup"
                    isOpen={!!restrictionKind}
                    kind={restrictionKind}
                    onCancel={handleRestrictionCancel}
                    onPrimary={handleRestrictionAddNewVehicle}
                />
            ) : null}
        </>
    );
};

export default ReactivateLoadLorry;
